/**
 * Scan-Quality Image Processing Pipeline
 *
 * Three modes:
 *   'bw'       — Adaptive thresholding (Sauvola) → crisp B&W scan
 *   'color'    — White balance + contrast enhancement → clean color
 *   'original' — No processing, just compress
 *
 * All modes output optimized JPEG blobs.
 */

// ─── Adobe-Grade Document Enhancement Pipeline ─────────────────

/**
 * Fast 2D background illumination estimator.
 * Downsamples the document image to ~1/8 scale, computes a wide local maximum filter
 * (radius 5 at 1/8 scale = ~40px full-scale, spanning across text paragraphs and ruled lines),
 * then smooths with a box blur. This accurately models uneven lighting, shadows, and paper tint.
 */
function estimateBackgroundIllumination(gray, w, h) {
  const scale = 8;
  const dw = Math.max(16, (w / scale) | 0);
  const dh = Math.max(16, (h / scale) | 0);
  const down = new Uint8Array(dw * dh);

  // Subsample
  for (let dy = 0; dy < dh; dy++) {
    const sy = Math.min(h - 1, dy * scale);
    const sRow = sy * w;
    const dRow = dy * dw;
    for (let dx = 0; dx < dw; dx++) {
      const sx = Math.min(w - 1, dx * scale);
      down[dRow + dx] = gray[sRow + sx];
    }
  }

  // Morphological Dilation / Local Max Filter (radius 5 = ~40px full-scale)
  // Ensures we reach white paper even across dense handwriting and notebook lines
  const dilated = new Uint8Array(dw * dh);
  const r = 5;
  for (let dy = 0; dy < dh; dy++) {
    for (let dx = 0; dx < dw; dx++) {
      let maxVal = 0;
      for (let ky = -r; ky <= r; ky++) {
        const ny = Math.min(dh - 1, Math.max(0, dy + ky));
        const nRow = ny * dw;
        for (let kx = -r; kx <= r; kx++) {
          const nx = Math.min(dw - 1, Math.max(0, dx + kx));
          const v = down[nRow + nx];
          if (v > maxVal) maxVal = v;
        }
      }
      dilated[dy * dw + dx] = maxVal;
    }
  }

  // Box Blur (radius 4) to smooth background transitions
  const smooth = new Uint8Array(dw * dh);
  for (let dy = 0; dy < dh; dy++) {
    for (let dx = 0; dx < dw; dx++) {
      let sum = 0, count = 0;
      for (let ky = -4; ky <= 4; ky++) {
        const ny = Math.min(dh - 1, Math.max(0, dy + ky));
        const nRow = ny * dw;
        for (let kx = -4; kx <= 4; kx++) {
          const nx = Math.min(dw - 1, Math.max(0, dx + kx));
          sum += dilated[nRow + nx];
          count++;
        }
      }
      smooth[dy * dw + dx] = (sum / count + 0.5) | 0;
    }
  }

  return { bgMap: smooth, dw, dh, scaleX: w / dw, scaleY: h / dh };
}

/**
 * Adobe Scan-grade Clean B&W Filter (Zero Ink Spread).
 *
 * 1. Illumination Normalization (Relative Reflectance):
 *    rho(x, y) = lum(x, y) / bg(x, y)
 *    Eliminates all room lighting shadows, phone silhouettes, and paper yellowing.
 *
 * 2. Reverse-Side Bleed-Through Elimination:
 *    Diffuse low-gradient shadows without sharp local stroke curvature from ink
 *    on the back of thin notebook paper are smoothly elevated to paper white.
 *
 * 3. Pencil & Fine Stroke Darkness Boost:
 *    Retains exact 1x stroke geometry without dilation or smudging, while boosting
 *    light graphite pencil into crisp, dark, 100% readable charcoal/black strokes.
 */
export function enhancePencilBW(gray, w, h) {
  const { bgMap, dw, dh, scaleX, scaleY } = estimateBackgroundIllumination(gray, w, h);
  const output = new Uint8Array(w * h);

  const rhoWhite = 0.90;
  const rhoBlack = 0.30;
  const rhoRange = rhoWhite - rhoBlack;

  for (let y = 0; y < h; y++) {
    const gy = y / scaleY;
    const y0 = Math.min(dh - 1, Math.max(0, Math.floor(gy)));
    const y1 = Math.min(dh - 1, y0 + 1);
    const fy = gy - y0;
    const y0Row = y0 * dw;
    const y1Row = y1 * dw;
    const rowOff = y * w;

    const yPrev = Math.max(0, y - 1) * w;
    const yNext = Math.min(h - 1, y + 1) * w;

    for (let x = 0; x < w; x++) {
      const gx = x / scaleX;
      const x0 = Math.min(dw - 1, Math.max(0, Math.floor(gx)));
      const x1 = Math.min(dw - 1, x0 + 1);
      const fx = gx - x0;

      // Bilinear interpolation of estimated background luminance
      const b00 = bgMap[y0Row + x0];
      const b10 = bgMap[y0Row + x1];
      const b01 = bgMap[y1Row + x0];
      const b11 = bgMap[y1Row + x1];
      const bg = Math.max(30, (1 - fy) * ((1 - fx) * b00 + fx * b10) + fy * ((1 - fx) * b01 + fx * b11));

      const lum = gray[rowOff + x];
      const rho = lum / bg;

      // First-derivative and second-derivative stroke/ridge estimation
      const xPrev1 = Math.max(0, x - 1);
      const xNext1 = Math.min(w - 1, x + 1);
      const grad1 =
        Math.abs(gray[rowOff + xNext1] - gray[rowOff + xPrev1]) +
        Math.abs(gray[yNext + x] - gray[yPrev + x]);

      const xP2 = Math.max(0, x - 2);
      const xN2 = Math.min(w - 1, x + 2);
      const yP2 = Math.max(0, y - 2) * w;
      const yN2 = Math.min(h - 1, y + 2) * w;
      const ridge = Math.max(0, (gray[rowOff + xP2] + gray[rowOff + xN2] + gray[yP2 + x] + gray[yN2 + x]) / 4 - lum);

      // Bleed-through suppression for faint diffuse ink from back of notebook sheet:
      // Diffuse bleed-through has low gradient and virtually zero ridge curvature.
      let effectiveRho = rho;
      if (rho >= 0.80) {
        if (grad1 < 12 && ridge < 10) {
          const d = (1 - grad1 / 12) * Math.min(1.0, (rho - 0.80) / 0.16);
          effectiveRho = Math.min(1.0, rho + d * (1.0 - rho));
        }
      }

      if (effectiveRho >= rhoWhite) {
        output[rowOff + x] = 255;
      } else if (effectiveRho <= rhoBlack) {
        output[rowOff + x] = 0;
      } else {
        const u = (rhoWhite - effectiveRho) / rhoRange;
        // Boost light pencil into clear, readable charcoal/black
        const darkBoost = Math.pow(u, 0.40);
        output[rowOff + x] = Math.max(0, Math.min(255, (255 * (1.0 - darkBoost) + 0.5) | 0));
      }
    }
  }

  // Subtle stroke boundary crisping (strictly on stroke pixels, not paper)
  const sharpened = new Uint8Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    const rPrev = (y - 1) * w;
    const rCurr = y * w;
    const rNext = (y + 1) * w;
    for (let x = 1; x < w - 1; x++) {
      const c = output[rCurr + x];
      if (c >= 245) {
        sharpened[rCurr + x] = 255;
        continue;
      }
      const neighbors =
        output[rPrev + x] + output[rNext + x] + output[rCurr + x - 1] + output[rCurr + x + 1];
      const laplacian = 4 * c - neighbors;
      const sharpVal = c + (laplacian * 0.10 + 0.5) | 0;
      sharpened[rCurr + x] = Math.max(0, Math.min(255, sharpVal));
    }
  }

  // Copy borders
  for (let x = 0; x < w; x++) {
    sharpened[x] = output[x];
    sharpened[(h - 1) * w + x] = output[(h - 1) * w + x];
  }
  for (let y = 0; y < h; y++) {
    sharpened[y * w] = output[y * w];
    sharpened[y * w + w - 1] = output[y * w + w - 1];
  }

  return sharpened;
}

// ─── Backward-compatible Sauvola Adaptive Thresholding ──────────

export function adaptiveThresholdBW(gray, w, h, windowSize = 25, k = 0.2) {
  return enhancePencilBW(gray, w, h);
}

// ─── Real Scan "Enhanced Mode" (True Optical Scanner Pipeline) ─

/**
 * Optical Flatbed Scanner-Grade Enhanced Processing ("Real Scan").
 *
 * 1. Flat-Field Illumination Normalization:
 *    Scales R, G, B channels by (255 / bg) across the 2D lighting estimation.
 *    Eliminates shadows and phone silhouettes while rendering paper uniform clean white.
 *
 * 2. Ruled Notebook Line Preservation:
 *    Identifies light cyan/blue printed ruled notebook lines and preserves them
 *    as subtle, delicate lines (never darkened or turned into harsh black bars).
 *
 * 3. Glare-Free Paper Tone & Bleed-Through Suppression:
 *    Renders paper as clean white (255) while eliminating diffuse reverse-side show-through
 *    on thin notebook paper without erasing front-side pencil or ink handwriting.
 *
 * 4. Continuous-Tone Handwriting & Pencil Contrast Deepening:
 *    Applies smooth progressive contrast expansion on stroke darkness so graphite pencil
 *    becomes clear, dark, and highly legible without harsh 1-bit thresholding cliffs.
 *
 * 5. 100% Authentic Ink Color Preservation & Vibrancy:
 *    Colored inks (blue ballpoint, red annotations, pink margin lines) retain their
 *    rich hue and natural continuous saturation.
 *
 * 6. Subtle Optical Edge Crisping (Unsharp Mask):
 *    Applies a gentle Laplacian unsharp mask strictly to stroke pixels for laser-sharp clarity.
 */
export function enhancedColorProcess(data, w, h) {
  // 1. Build grayscale buffer for background estimation
  const gray = new Uint8Array(w * h);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
  }

  const { bgMap, dw, dh, scaleX, scaleY } = estimateBackgroundIllumination(gray, w, h);
  const tempOut = new Uint8ClampedArray(data.length);

  for (let y = 0; y < h; y++) {
    const gy = y / scaleY;
    const y0 = Math.min(dh - 1, Math.max(0, Math.floor(gy)));
    const y1 = Math.min(dh - 1, y0 + 1);
    const fy = gy - y0;
    const y0Row = y0 * dw;
    const y1Row = y1 * dw;
    const rowOff = y * w;

    const yPrev = Math.max(0, y - 1) * w;
    const yNext = Math.min(h - 1, y + 1) * w;
    const yP2 = Math.max(0, y - 2) * w;
    const yN2 = Math.min(h - 1, y + 2) * w;

    for (let x = 0; x < w; x++) {
      const gx = x / scaleX;
      const x0 = Math.min(dw - 1, Math.max(0, Math.floor(gx)));
      const x1 = Math.min(dw - 1, x0 + 1);
      const fx = gx - x0;

      const b00 = bgMap[y0Row + x0];
      const b10 = bgMap[y0Row + x1];
      const b01 = bgMap[y1Row + x0];
      const b11 = bgMap[y1Row + x1];
      const bg = Math.max(30, (1 - fy) * ((1 - fx) * b00 + fx * b10) + fy * ((1 - fx) * b01 + fx * b11));

      const idx = (rowOff + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const lum = gray[rowOff + x];

      // Flat-field normalized channels: scale by 255 / bg
      const invBg = 255.0 / bg;
      const rNorm = Math.min(255, r * invBg);
      const gNorm = Math.min(255, g * invBg);
      const bNorm = Math.min(255, b * invBg);
      const lNorm = Math.min(255, (rNorm * 77 + gNorm * 150 + bNorm * 29) >> 8);

      // Chroma (color saturation)
      const chroma = Math.max(Math.abs(rNorm - gNorm), Math.abs(gNorm - bNorm), Math.abs(bNorm - rNorm));

      // Ruled line detection: light cyan/blue notebook lines
      const isRuledLine = (bNorm - rNorm >= 8 || gNorm - rNorm >= 6) && (lNorm >= 195);

      // Local gradient and curvature
      const xPrev1 = Math.max(0, x - 1);
      const xNext1 = Math.min(w - 1, x + 1);
      const grad1 =
        Math.abs(gray[rowOff + xNext1] - gray[rowOff + xPrev1]) +
        Math.abs(gray[yNext + x] - gray[yPrev + x]);

      const xP2 = Math.max(0, x - 2);
      const xN2 = Math.min(w - 1, x + 2);
      const ridge = Math.max(0, (gray[rowOff + xP2] + gray[rowOff + xN2] + gray[yP2 + x] + gray[yN2 + x]) / 4 - lum);

      // Diffuse bleed-through detection: low gradient & low ridge in near-paper region
      const isBleedThrough = (lNorm >= 195) && (grad1 < 12 && ridge < 10);

      // Paper white threshold
      if (lNorm >= 235 || isBleedThrough) {
        tempOut[idx]     = 255;
        tempOut[idx + 1] = 255;
        tempOut[idx + 2] = 255;
        tempOut[idx + 3] = 255;
        continue;
      }

      // If it's a ruled notebook line, preserve subtle light cyan/blue tone
      if (isRuledLine) {
        tempOut[idx]     = Math.min(255, Math.max(0, (rNorm * 0.95 + 12 + 0.5) | 0));
        tempOut[idx + 1] = Math.min(255, Math.max(0, (gNorm * 0.95 + 12 + 0.5) | 0));
        tempOut[idx + 2] = Math.min(255, Math.max(0, (bNorm * 0.95 + 12 + 0.5) | 0));
        tempOut[idx + 3] = 255;
        continue;
      }

      // Continuous tone mapping
      const W_white = 235;
      const B_black = 30;
      const range = W_white - B_black;
      const t = Math.max(0, Math.min(1.0, (lNorm - B_black) / range));

      let lOut;
      if (chroma <= 8) {
        // Neutral handwriting (pencil / black ink):
        // Smooth S-curve contrast boost that darkens pencil writing into crisp, readable charcoal
        // without flattening into harsh black!
        const s = 1.0 - t; // stroke darkness in [0, 1]
        const normS = Math.max(0, (s - 0.02) / 0.98);
        const boostedS = Math.pow(normS, 0.22);
        lOut = Math.round(255 * (1.0 - boostedS * 0.92));
      } else {
        // Colored inks (blue ballpoint, red pen, pink margin, markers):
        // Gamma 1.15 deepens strokes while maintaining authentic continuous color saturation
        lOut = Math.round(Math.pow(t, 1.15) * 255);
      }
      lOut = Math.max(15, Math.min(255, lOut));

      // Reconstruct RGB preserving exact hue
      const scale = lOut / Math.max(1, lNorm);
      let rOut = rNorm * scale;
      let gOut = gNorm * scale;
      let bOut = bNorm * scale;

      // Vibrancy boost for colored ink
      if (chroma > 10) {
        const satBoost = 1.25;
        rOut = lOut + (rOut - lOut) * satBoost;
        gOut = lOut + (gOut - lOut) * satBoost;
        bOut = lOut + (bOut - lOut) * satBoost;
      }

      tempOut[idx]     = Math.max(0, Math.min(255, (rOut + 0.5) | 0));
      tempOut[idx + 1] = Math.max(0, Math.min(255, (gOut + 0.5) | 0));
      tempOut[idx + 2] = Math.max(0, Math.min(255, (bOut + 0.5) | 0));
      tempOut[idx + 3] = 255;
    }
  }

  // 2. Subtle Laplacian unsharp mask on stroke pixels for crisp clarity
  for (let y = 1; y < h - 1; y++) {
    const rPrev = (y - 1) * w;
    const rCurr = y * w;
    const rNext = (y + 1) * w;
    for (let x = 1; x < w - 1; x++) {
      const idx = (rCurr + x) * 4;
      if (tempOut[idx] >= 252 && tempOut[idx + 1] >= 252 && tempOut[idx + 2] >= 252) {
        data[idx] = 255;
        data[idx + 1] = 255;
        data[idx + 2] = 255;
        data[idx + 3] = 255;
        continue;
      }
      for (let c = 0; c < 3; c++) {
        const val = tempOut[idx + c];
        const n =
          tempOut[(rPrev + x) * 4 + c] +
          tempOut[(rNext + x) * 4 + c] +
          tempOut[(rCurr + x - 1) * 4 + c] +
          tempOut[(rCurr + x + 1) * 4 + c];
        const lap = 4 * val - n;
        data[idx + c] = Math.max(0, Math.min(255, (val + lap * 0.12 + 0.5) | 0));
      }
      data[idx + 3] = 255;
    }
  }

  // Copy borders
  for (let x = 0; x < w; x++) {
    for (let c = 0; c < 4; c++) {
      data[x * 4 + c] = tempOut[x * 4 + c];
      data[((h - 1) * w + x) * 4 + c] = tempOut[((h - 1) * w + x) * 4 + c];
    }
  }
  for (let y = 0; y < h; y++) {
    for (let c = 0; c < 4; c++) {
      data[(y * w) * 4 + c] = tempOut[(y * w) * 4 + c];
      data[(y * w + w - 1) * 4 + c] = tempOut[(y * w + w - 1) * 4 + c];
    }
  }

  return data;
}

// ─── Canvas Resize ──────────────────────────────────────────────

function resizeCanvas(canvas, maxWidth) {
  if (canvas.width <= maxWidth) return canvas;

  const scale = maxWidth / canvas.width;
  const out = document.createElement("canvas");
  out.width = maxWidth;
  out.height = Math.round(canvas.height * scale);
  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

// ─── Public: Process Document Scan ──────────────────────────────

/**
 * Process a perspective-corrected canvas into a scan-quality image.
 *
 * @param {HTMLCanvasElement} canvas  Perspective-corrected document
 * @param {'bw'|'color'|'original'} mode  Processing mode
 * @returns {Promise<Blob>}  Optimized JPEG blob
 */
export const processImage = async (canvas, mode = "color") => {
  return new Promise((resolve) => {
    try {
      const maxWidth = 1600;

      // Resize if needed
      const resized = resizeCanvas(canvas, maxWidth);
      const w = resized.width;
      const h = resized.height;

      if (mode === "original") {
        // No processing, just compress
        resized.toBlob((blob) => resolve(blob), "image/jpeg", 0.88);
        return;
      }

      const rCtx = resized.getContext("2d");
      const rImgData = rCtx.getImageData(0, 0, w, h);

      if (mode === "bw") {
        // Grayscale → adaptive threshold
        const rGray = new Uint8Array(w * h);
        for (let i = 0, j = 0; i < rImgData.data.length; i += 4, j++) {
          rGray[j] = (rImgData.data[i] * 77 + rImgData.data[i + 1] * 150 + rImgData.data[i + 2] * 29) >> 8;
        }

        const thresholded = enhancePencilBW(rGray, w, h);

        // Write back to image data
        for (let i = 0, j = 0; i < rImgData.data.length; i += 4, j++) {
          rImgData.data[i] = thresholded[j];
          rImgData.data[i + 1] = thresholded[j];
          rImgData.data[i + 2] = thresholded[j];
          rImgData.data[i + 3] = 255;
        }

        rCtx.putImageData(rImgData, 0, 0);
        // Clean B&W scans with razor-sharp text
        resized.toBlob((blob) => resolve(blob), "image/jpeg", 0.86);
      } else if (mode === "color") {
        // White balance + contrast enhancement
        enhancedColorProcess(rImgData.data, w, h);
        rCtx.putImageData(rImgData, 0, 0);
        resized.toBlob((blob) => resolve(blob), "image/jpeg", 0.88);
      }
    } catch (err) {
      console.error("processImage error:", err);
      // Fallback: return the raw canvas as JPEG
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
    }
  });
};

/**
 * Legacy compatibility wrapper: accepts a data URL, returns a data URL.
 * Used by existing code paths that haven't been migrated to Blob workflow.
 */
export const processImageLegacy = async (imageDataUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const blob = await processImage(canvas, "color");
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    };
    img.onerror = () => resolve(imageDataUrl);
    img.src = imageDataUrl;
  });
};

/**
 * Estimate file size of a Blob in KB.
 */
export const estimateFileSize = (blob) => {
  if (blob instanceof Blob) return Math.round((blob.size / 1024) * 10) / 10;
  // Legacy: base64 string
  const base64Data = String(blob).replace(/^data:image\/\w+;base64,/, "");
  return Math.round(((base64Data.length * 3) / 4 / 1024) * 10) / 10;
};